import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const LogoCarousel = ({ logos }) => {
  const controls = useAnimation();
  const [isPaused, setIsPaused] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Animation pour le défilement automatique
  const carouselVariants = {
    animate: {
      x: ["-5%", "-50%"],
      transition: {
        x: {
          repeat: Infinity,
          repeatType: "loop",
          duration: 25,
          ease: "linear"
        }
      }
    },
    paused: {
      x: "calc(var(--x, 0) * 1px)",
      transition: {
        duration: 0.2,
        ease: "easeOut"
      }
    }
  };
  
  // Effet pour contrôler l'animation en fonction de l'état de pause
  useEffect(() => {
    if (isPaused) {
      // Récupérer la position actuelle pour la figer
      const element = document.querySelector('.logo-carousel');
      if (element) {
        const computedStyle = window.getComputedStyle(element);
        const matrix = new DOMMatrix(computedStyle.transform);
        element.style.setProperty('--x', matrix.m41);
        controls.start('paused');
      }
    } else {
      controls.start('animate');
    }
  }, [isPaused, controls]);

  return (
    <div className="w-full overflow-hidden py-12 relative" ref={ref}>
      {isPaused && (
        <motion.div 
          className="absolute top-0 right-4 bg-primary/80 text-white px-3 py-1 rounded-b-md text-sm font-medium z-10"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          Défilement en pause
        </motion.div>
      )}
      <motion.div
        className="flex items-center"
        variants={containerVariants}
        initial="hidden"
        animate={controls}
      >
        <motion.div
          className="flex space-x-12 items-center logo-carousel"
          variants={carouselVariants}
          animate={isPaused ? 'paused' : 'animate'}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Première série de logos */}
          {logos.map((logo, index) => (
            <motion.div
              key={`logo-${index}`}
              className="flex-shrink-0 h-24 w-auto mx-6 grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer hover:bg-white/30 hover:rounded-lg hover:p-2 hover:shadow-lg"
              variants={itemVariants}
              whileHover={{ scale: 1.15, y: -5 }}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="h-full w-auto object-contain drop-shadow-md"
              />
            </motion.div>
          ))}
          
          {/* Répétition des logos pour créer un effet de défilement infini */}
          {logos.map((logo, index) => (
            <motion.div
              key={`logo-repeat-${index}`}
              className="flex-shrink-0 h-24 w-auto mx-6 grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer hover:bg-white/30 hover:rounded-lg hover:p-2 hover:shadow-lg"
              variants={itemVariants}
              whileHover={{ scale: 1.15, y: -5 }}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="h-full w-auto object-contain drop-shadow-md"
              />
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LogoCarousel;